/**
 * Regras de upload: 1 ativo por categoria compartilhada;
 * receita/ANVISA = 1 por transação, nova só após aprovação + liberação.
 * @module supportingDocumentUploadRules
 */

import type { PatientSupportingDocumentTypeId } from "../types";
import {
  createDocumentTransactionId,
  isDocumentValidityExpiredDate,
} from "./documentExtensions";

export const SHARED_SUPPORTING_DOCUMENT_TYPES: PatientSupportingDocumentTypeId[] =
  ["personal_document", "proof_of_residence"];

export const TRANSACTION_SUPPORTING_DOCUMENT_TYPES: PatientSupportingDocumentTypeId[] =
  ["medical_prescription", "anvisa_import_authorization"];

export function isSharedSupportingDocumentType(
  typeId: PatientSupportingDocumentTypeId,
): boolean {
  return SHARED_SUPPORTING_DOCUMENT_TYPES.includes(typeId);
}

export function isTransactionSupportingDocumentType(
  typeId: PatientSupportingDocumentTypeId,
): boolean {
  return TRANSACTION_SUPPORTING_DOCUMENT_TYPES.includes(typeId);
}

export interface ActiveSupportingDocumentLike {
  isAttested?: boolean;
  isRejected?: boolean;
  validUntil?: string;
  transactionId?: string;
}

export interface CanAddSupportingDocumentParams {
  typeId: PatientSupportingDocumentTypeId;
  /** Documentos ativos da categoria (já hidratados). */
  activeOfType: ActiveSupportingDocumentLike[];
  /** true quando a transação mais recente desta categoria já foi aprovada e liberada. */
  previousTransactionReleased?: boolean;
  /** true quando o envio é substituição explícita (relatesTo replaces). */
  isReplace?: boolean;
}

export interface CanAddSupportingDocumentResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Valida se o paciente pode adicionar (ou só substituir) um documento.
 * Pendente de aprovação bloqueia novo envio. Substituição explícita é permitida.
 */
export function canAddSupportingDocument(
  params: CanAddSupportingDocumentParams,
): CanAddSupportingDocumentResult {
  if (params.isReplace) {
    return { allowed: true };
  }

  const active = params.activeOfType ?? [];
  const hasRejected = active.some((doc) => doc.isRejected);
  if (hasRejected) {
    return {
      allowed: false,
      reason:
        "Há um documento recusado. Use Substituir para enviar outro arquivo.",
    };
  }

  const hasPendingApproval = active.some(
    (doc) =>
      !doc.isAttested &&
      !doc.isRejected &&
      !isDocumentValidityExpiredDate(doc.validUntil),
  );
  if (hasPendingApproval) {
    return {
      allowed: false,
      reason:
        "Já existe um documento aguardando aprovação. Aguarde a verificação para enviar outro.",
    };
  }

  const hasApprovedActive = active.some(
    (doc) =>
      doc.isAttested && !isDocumentValidityExpiredDate(doc.validUntil),
  );

  if (isSharedSupportingDocumentType(params.typeId)) {
    if (hasApprovedActive) {
      return {
        allowed: false,
        reason:
          "Já existe um documento ativo nesta categoria. Use Substituir ou aguarde o vencimento.",
      };
    }
    return { allowed: true };
  }

  if (isTransactionSupportingDocumentType(params.typeId)) {
    if (hasApprovedActive && !params.previousTransactionReleased) {
      return {
        allowed: false,
        reason:
          "Já existe uma receita/autorização nesta transação. Envie uma nova somente após a liberação da anterior.",
      };
    }
    return { allowed: true };
  }

  return { allowed: true };
}

/**
 * Receita/ANVISA: reusa o id só se o irmão estiver numa transação ainda incompleta.
 * Par já completo → novo UUID (nova transação). Substituição mantém o id.
 */
export function resolveUploadTransactionId(params: {
  typeId: PatientSupportingDocumentTypeId;
  documentsByType: Partial<
    Record<
      PatientSupportingDocumentTypeId,
      Array<{ transactionId?: string }>
    >
  >;
  replacingTransactionId?: string;
}): string {
  const replacingId = params.replacingTransactionId?.trim();
  if (replacingId) return replacingId;

  if (!isTransactionSupportingDocumentType(params.typeId)) {
    return createDocumentTransactionId();
  }

  const siblingType: PatientSupportingDocumentTypeId =
    params.typeId === "medical_prescription"
      ? "anvisa_import_authorization"
      : "medical_prescription";

  const ownIds = new Set(
    (params.documentsByType[params.typeId] ?? [])
      .map((doc) => doc.transactionId?.trim())
      .filter((id): id is string => Boolean(id)),
  );

  const openSibling = (params.documentsByType[siblingType] ?? []).find((doc) => {
    const txId = doc.transactionId?.trim();
    return Boolean(txId && !ownIds.has(txId));
  });

  return openSibling?.transactionId?.trim() || createDocumentTransactionId();
}

export function getAddDocumentBlockedMessage(
  typeId: PatientSupportingDocumentTypeId,
): string {
  if (isSharedSupportingDocumentType(typeId)) {
    return "Só é permitido 1 documento ativo. Use Substituir para trocar o arquivo.";
  }
  return "Aguarde a aprovação do documento enviado antes de enviar outro.";
}
